/*
 * firewall change management service
 * manages firewall change requests
 */

import { supabase } from './supabase'
import { FirewallChangeRequestRow } from '@/types'

/*
 * list firewall change requests
 */
export async function listFirewallChangeRequests(options?: {
  shipId?: string
  status?: FirewallChangeRequestRow['status']
  approvalStatus?: FirewallChangeRequestRow['approval_status']
  limit?: number
}): Promise<FirewallChangeRequestRow[]> {
  try {
    let query = supabase
      .from('firewall_change_requests')
      .select('*')
      .order('created_at', { ascending: false })

    if (options?.shipId) query = query.eq('ship_id', options.shipId)
    if (options?.status) query = query.eq('status', options.status)
    if (options?.approvalStatus) query = query.eq('approval_status', options.approvalStatus)
    if (options?.limit) query = query.limit(options.limit)

    const { data, error } = await query
    if (error) {
      if (error.code === '42501' || error.message?.includes('policy')) {
        throw new Error('Access denied. Please check your permissions.')
      }
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        throw new Error('Firewall change requests table does not exist.')
      }
      throw error
    }
    return data || []
  } catch (error: any) {
    console.error('Error fetching firewall change requests:', error)
    throw error
  }
}

/* get a single firewall change request */
export async function getFirewallChangeRequestDetail(firewallChangeRequestId: string): Promise<FirewallChangeRequestRow | null> {
  try {
    const { data, error } = await supabase
      .from('firewall_change_requests')
      .select('*')
      .eq('id', firewallChangeRequestId)
      .limit(1)

    if (error) throw error
    return data && data.length > 0 ? data[0] : null
  } catch (error: any) {
    console.error('[FIREWALL] Error fetching firewall change request detail:', error)
    throw error
  }
}

/* create a new firewall change request and generate a submission token */
export async function createFirewallChangeRequest(data: Partial<FirewallChangeRequestRow>): Promise<{
  success: boolean
  data?: FirewallChangeRequestRow
  submissionUrl?: string
  error?: string
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    // generate request number
    let requestNo: string = 'FW-CR-001'
    try {
      const { data: existingRequests } = await supabase
        .from('firewall_change_requests')
        .select('id')
        .order('created_at', { ascending: false })
      if (existingRequests) {
        requestNo = `FW-CR-${String(existingRequests.length + 1).padStart(3, '0')}`
      }
    } catch { /* fallback */ }

    const firewallChangeRequestData = {
      ...data,
      request_no: requestNo,
      created_by: user.id,
      status: data.status || 'draft',
      approval_status: data.approval_status || 'review',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    const { data: result, error } = await supabase
      .from('firewall_change_requests')
      .insert([firewallChangeRequestData])
      .select()

    if (error) throw error
    if (!result || result.length === 0) throw new Error('Failed to create firewall change request')

    const createdRequest = result[0]

    // generate submission token
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 7)

    const { data: tokenData, error: tokenError } = await supabase
      .from('firewall_change_request_submission_tokens')
      .insert([{
        firewall_change_request_id: createdRequest.id,
        token,
        created_by: user.id,
        expires_at: expiresAt.toISOString(),
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()

    if (tokenError) {
      console.error('[FIREWALL] Error creating submission token:', tokenError)
    } else if (tokenData && tokenData.length > 0) {
      try {
        const { error: lookupError } = await supabase
          .rpc('insert_firewall_token_lookup', {
            token_text: token,
            schema_name: 'public'
          })
        if (lookupError) console.error('[FIREWALL] Error adding token to lookup table:', lookupError)
      } catch { /* ignore */ }

      await supabase
        .from('firewall_change_requests')
        .update({ submission_token_id: tokenData[0].id })
        .eq('id', createdRequest.id)
    }

    const submissionUrl = `${window.location.origin}/submit-firewall-change?token=${token}`
    return { success: true, data: createdRequest, submissionUrl }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to create firewall change request' }
  }
}

/* update an existing firewall change request */
export async function updateFirewallChangeRequest(
  firewallChangeRequestId: string,
  updates: Partial<FirewallChangeRequestRow>
): Promise<{ success: boolean; data?: FirewallChangeRequestRow; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('firewall_change_requests')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', firewallChangeRequestId)
      .select()

    if (error) throw error
    return { success: true, data: data && data.length > 0 ? data[0] : undefined }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to update firewall change request' }
  }
}

/* submit a firewall change request for review */
export async function submitFirewallChangeRequest(firewallChangeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('firewall_change_requests')
      .update({ status: 'under_review', updated_at: new Date().toISOString() })
      .eq('id', firewallChangeRequestId)

    if (error) throw error
    return { success: true, message: 'Firewall change request submitted for review' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to submit firewall change request' }
  }
}

/* approve a firewall change request */
export async function approveFirewallChangeRequest(
  firewallChangeRequestId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('firewall_change_requests')
      .update({
        approval_status: 'approved',
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', firewallChangeRequestId)

    if (error) throw error
    return { success: true, message: 'Firewall change request approved successfully' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to approve firewall change request' }
  }
}

/* reject a firewall change request */
export async function rejectFirewallChangeRequest(
  firewallChangeRequestId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('firewall_change_requests')
      .update({
        approval_status: 'rejected',
        status: 'under_review',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        rejection_reason: reason || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', firewallChangeRequestId)

    if (error) throw error
    return { success: true, message: 'Firewall change request rejected' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to reject firewall change request' }
  }
}

/* mark a firewall change request as implemented */
export async function markFirewallImplemented(firewallChangeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('firewall_change_requests')
      .update({
        status: 'implemented',
        implemented_by: user.id,
        implemented_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', firewallChangeRequestId)

    if (error) throw error
    return { success: true, message: 'Firewall change request marked as implemented' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to mark firewall change request as implemented' }
  }
}

/* close a firewall change request */
export async function closeFirewallChangeRequest(firewallChangeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('firewall_change_requests')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', firewallChangeRequestId)

    if (error) throw error
    return { success: true, message: 'Firewall change request closed' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to close firewall change request' }
  }
}

/* get submission token for a firewall change request */
export async function getFirewallSubmissionToken(firewallChangeRequestId: string): Promise<{ success: boolean; token?: string; url?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('firewall_change_request_submission_tokens')
      .select('*')
      .eq('firewall_change_request_id', firewallChangeRequestId)
      .eq('is_active', true)
      .limit(1)

    if (error) throw error
    if (!data || data.length === 0) return { success: false, error: 'No active submission token found' }

    const tokenRecord = data[0]
    if (new Date(tokenRecord.expires_at) <= new Date()) {
      return { success: false, error: 'Submission token has expired' }
    }

    const url = `${window.location.origin}/submit-firewall-change?token=${tokenRecord.token}`
    return { success: true, token: tokenRecord.token, url }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to get firewall submission token' }
  }
}

/* regenerate submission token for a firewall change request */
export async function regenerateFirewallSubmissionToken(firewallChangeRequestId: string): Promise<{ success: boolean; token?: string; url?: string; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    // deactivate old tokens
    const { data: oldTokens } = await supabase
      .from('firewall_change_request_submission_tokens')
      .select('id')
      .eq('firewall_change_request_id', firewallChangeRequestId)

    if (oldTokens) {
      for (const oldToken of oldTokens) {
        await supabase
          .from('firewall_change_request_submission_tokens')
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .eq('id', oldToken.id)
      }
    }

    // generate new token
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 7)

    const { data: tokenData, error: tokenError } = await supabase
      .from('firewall_change_request_submission_tokens')
      .insert([{
        firewall_change_request_id: firewallChangeRequestId,
        token,
        created_by: user.id,
        expires_at: expiresAt.toISOString(),
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()

    if (tokenError) throw tokenError
    if (!tokenData || tokenData.length === 0) throw new Error('Failed to create new token')

    try {
      const { error: lookupError } = await supabase
        .rpc('insert_firewall_token_lookup', {
          token_text: token,
          schema_name: 'public'
        })
      if (lookupError) console.error('[FIREWALL] Error adding token to lookup table:', lookupError)
    } catch { /* ignore */ }

    await supabase
      .from('firewall_change_requests')
      .update({ submission_token_id: tokenData[0].id })
      .eq('id', firewallChangeRequestId)

    const url = `${window.location.origin}/submit-firewall-change?token=${token}`
    return { success: true, token, url }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to regenerate firewall submission token' }
  }
}
